// Blog posts. `content` is markdown (react-markdown + GFM tables/links).
// Slugs are live URLs (/blog/<slug>) — do NOT change an existing slug.
// The WhatsApp CTA links use the real number and are picked up by the
// whatsapp_click tracking, same as the site buttons.
const WA = 'https://wa.me/9779764581986'

export const posts = [
  {
    slug: 'manaslu-permit-cost-2025',
    title: 'Manaslu Circuit permit cost 2026 — the complete breakdown',
    excerpt: 'The four permits Manaslu Circuit requires, what each costs in 2026, and how I get them sorted so you never set foot in a government office.',
    category: 'Permits',
    date: 'June 2, 2026',
    readTime: '6 min read',
    emoji: '📋',
    content: `
**Short version:** Manaslu Circuit needs four permits, totalling roughly **$199–$259** depending on season. If you trek with me, that cost is inside your package and I do all the paperwork — you never queue at an office.

Here is the full breakdown for 2026.

## 1. Manaslu Restricted Area Permit (the important one)

This is the permit unique to Manaslu, and the reason a guide is mandatory here.

**Cost:** USD **$100 for the first 7 days + $15/day after** in peak season (September–November); **$75 + $10/day** the rest of the year.

For a standard 14-day circuit that works out to roughly **$205 in peak season, $145 off-peak**.

**It must be issued in Kathmandu, before you leave** — you cannot buy it on the trail, and it can only be issued through a registered agency. I handle this for every client.

## 2. Manaslu Conservation Area Permit (MCAP)

**Cost:** NPR 3,000 (about **$23 USD**). Issued by the National Trust for Nature Conservation.

## 3. Annapurna Conservation Area Permit (ACAP)

**Cost:** NPR 3,000 (about **$23 USD**). Required because the circuit exits into the Annapurna Conservation Area near Dharapani.

## 4. Chumnubri Rural Municipality Entry Permit

**Cost:** NPR 1,000 (about **$8 USD**). A local municipal fee, usually collected on the trail around Jagat or Philim.

## Total permit cost

- **Restricted Area (14 days):** ~$205 peak season / ~$145 off-peak
- **MCAP:** ~$23
- **ACAP:** ~$23
- **Chumnubri:** ~$8
- **Total: ~$259 peak season · ~$199 off-peak**

## How I handle it

All permits are included in my packages. I collect your paperwork, make the applications, and hand you the permits on departure day. If you are self-organising instead, leave one full day in Kathmandu to get them done.

**Not sure which season you fall in, or what your dates cost?** [Message me on WhatsApp](${WA}) — I answer every message myself.
    `.trim(),
  },
  {
    slug: 'manaslu-vs-annapurna',
    title: 'Manaslu vs Annapurna Circuit — which should you choose?',
    excerpt: "I've guided both hundreds of times. An honest comparison — crowds, culture, cost, and difficulty — from someone who has crossed both passes more times than he can count.",
    category: 'Planning',
    date: 'May 18, 2026',
    readTime: '8 min read',
    emoji: '🏔️',
    content: `
**Short version:** Both are exceptional. Choose **Manaslu** for solitude and raw, Tibetan-influenced culture; choose **Annapurna** for easier logistics, better teahouses, and the famous Thorong La + Muktinath. First trek in Nepal? Annapurna is more forgiving. Done a big trek already? Manaslu.

## At a glance

- **High point** — Manaslu: Larkya La, 5,106m · Annapurna: Thorong La, 5,416m
- **Crowds (October)** — Manaslu: dozens a day · Annapurna: hundreds a day
- **Teahouses** — Manaslu: basic, genuine · Annapurna: more comfort, more choice
- **Permits** — Manaslu: restricted area (guide required) · Annapurna: conservation area
- **Feel** — Manaslu: exploration · Annapurna: classic

## Manaslu is better if you want

- Far fewer people on the trail
- More authentic Tibetan-influenced villages
- A genuine sense of exploration and remoteness
- The feeling that you earned it

## Annapurna is better if you want

- More infrastructure — better teahouses, more food options
- Easier logistics and more transport options
- Thorong La and Muktinath temple
- A slightly more affordable, more forgiving first trek

## The crowd question

This is the biggest practical difference. In October peak season, popular sections of the Annapurna Circuit see hundreds of trekkers a day. Manaslu sees dozens. If you came to Nepal for solitude, Manaslu wins — it isn't close.

## My honest recommendation

If you have already done Annapurna or Everest Base Camp — do **Manaslu**. It feels like the Nepal you were looking for the first time. If this is your first Nepal trek — **Annapurna** is gentler on logistics. And if you genuinely cannot decide — do Manaslu. Nobody regrets it.

**Want me to match a trek to your dates and experience?** [Message me on WhatsApp](${WA}).
    `.trim(),
  },
  {
    slug: 'manaslu-packing-list',
    title: 'Manaslu Circuit packing list — what I actually carry',
    excerpt: 'Not the generic list off every trekking website. The kit I personally recommend after 40+ Larkya La crossings — including what you can safely leave at home.',
    category: 'Gear',
    date: 'May 4, 2026',
    readTime: '5 min read',
    emoji: '🎒',
    content: `
**Short version:** Real down jacket, broken-in boots, trekking poles, a headlamp, and Diamox are the non-negotiables. Keep your total under 15kg and your daypack under 8kg. Everything can be rented in Thamel for a few dollars a day.

After crossing Larkya La more than 40 times, I have a clear opinion about what you need — and what just adds weight.

## The non-negotiables

**Clothing**
- Down jacket — real down, not synthetic (it hits −15°C at Dharmasala)
- Waterproof shell jacket and trousers
- Merino wool base layers ×2
- Fleece mid-layer
- Warm hat, gloves, buff
- Trekking boots — broken in (never bring brand-new boots)
- Gaiters for snow on Larkya La in shoulder season

**Gear**
- Trekking poles — mandatory for the Larkya La descent
- Headlamp + spare batteries (3am start on pass day)
- Water purification — tablets or a Steripen
- Sleeping-bag liner (blankets are fine; a liner is comfort insurance)

**Health**
- Diamox for altitude — carry it even if you don't plan to use it
- Ibuprofen and paracetamol
- Blister kit
- Rehydration sachets

## What you do NOT need

- More than two pairs of trekking trousers
- A laptop
- More than 15kg total — porters carry 25kg max, keep your daypack under 8kg
- Fancy trekking outfits — teahouse owners genuinely do not care what you look like

## Renting in Kathmandu

Everything above rents in Thamel for **$3–8 a day** — down jacket, poles, sleeping bag, the lot, at good quality. I can point you to the shops I trust.

**Want a packing list tailored to your season and dates?** [Message me on WhatsApp](${WA}).
    `.trim(),
  },
  {
    slug: 'larkya-la-pass-guide',
    title: 'Crossing Larkya La Pass — what nobody tells you',
    excerpt: 'The 5,106m high point of Manaslu Circuit, hour by hour: the 3am start in the dark, how cold it truly gets, and the exact signs that make me turn a client back.',
    category: 'Trail Guide',
    date: 'April 20, 2026',
    readTime: '7 min read',
    emoji: '⛰️',
    content: `
**Short version:** You leave Dharmasala (4,460m) at 3am, reach Larkya La (5,106m) in 4–5 hours around sunrise, then descend four hours to green Bimthang. Expect −10°C to −15°C, packed snow above 4,800m in season, and a hard but unforgettable day. Real down and trekking poles are essential.

I have crossed Larkya La more than 40 times. Here is what actually happens.

## The night before

You sleep at Dharmasala teahouse, 4,460m. Dinner at 6pm, lights out by 7:30pm. Nobody sleeps well — altitude, nerves, cold. That's normal. The teahouse is basic, one blanket; use your down jacket as a second.

## 3am — the start

Your headlamp is the only light for the first two hours. The trail is rocky, then snow. In October there's usually packed snow from about 4,800m up. Temperature: **−10°C to −15°C**, and with windchill on the pass it can feel like −25°C. This is exactly why real down is non-negotiable.

## The summit

You reach Larkya La (**5,106m**) roughly 4–5 hours in, usually just before sunrise. Himlung, Cheo Himal, and Annapurna II in the dawn light are the moment everyone comes for. Stay 10–15 minutes, no more — the descent is long.

## When I turn clients back

I watch for three things: a severe headache that won't respond to ibuprofen, loss of coordination, or confusion. Any one of them and we descend immediately — no negotiation. In 40+ crossings I've turned back twice. Both clients thanked me later.

## The descent to Bimthang

Four hours downhill. Knees complain. But the valley below — green, warm, yaks grazing — feels like another planet after the pass. The hardest part of the Manaslu Circuit is behind you.

**Planning your Larkya La crossing?** [Message me on WhatsApp](${WA}) — happy to talk you through the pass day.
    `.trim(),
  },
  {
    slug: 'solo-trekking-manaslu',
    title: 'Solo trekking Manaslu Circuit — is it possible in 2025?',
    excerpt: 'Guides are now mandatory by law on Manaslu. But solo travellers can absolutely still do it — here is exactly how, whether it is safe for solo women, and what it costs.',
    category: 'Planning',
    date: 'April 6, 2026',
    readTime: '6 min read',
    emoji: '🚶',
    content: `
**Short version:** Since 2023 you cannot legally trek Manaslu alone — a licensed guide is required. But you do **not** need a group. Join a scheduled small group (from $1,100) or take a private departure (from ~$1,400). It's safe, including for solo women.

## What the law actually means

Nepal now requires all foreign trekkers to hire a licensed guide on most routes, Manaslu included. You need a guide — you do not need a group.

Solo travellers have two options with me:

**Option 1 — Join a scheduled departure**
I match you into a small group (2–6 people). You pay the standard per-person price, meet the group in Kathmandu, and most people love it — you make friends, share the experience, split nothing on price. This is what most solo trekkers choose.

**Option 2 — Private departure**
Just you and me (and a porter if you want). Flexible dates, fully customised pace. Roughly **$300–$400 more** than the group price for Manaslu.

## Is it safe for solo women?

Yes. Nepal is genuinely one of the safest countries for solo female travellers, and I've guided dozens of solo women on Manaslu. You're never truly alone on the trail — teahouse owners, other trekkers, and me, and I'm always reachable on WhatsApp.

## Cost for solo trekkers

- **Group departure:** from $1,100 (same as everyone)
- **Private departure:** from $1,400–$1,500

**Tell me your dates and I'll match you to the right option.** [Message me on WhatsApp](${WA}).
    `.trim(),
  },
  {
    slug: 'kathmandu-before-trek',
    title: 'Things to do in Kathmandu before your trek — a 2-day guide',
    excerpt: 'A practical two-day Kathmandu plan from someone who lives here: gear shopping, permits, gentle acclimatization walks, and where to eat dal bhat like a local.',
    category: 'Kathmandu',
    date: 'March 15, 2026',
    readTime: '5 min read',
    emoji: '🏙️',
    content: `
**Short version:** Most trekkers land 1–2 days early. Spend day one on gear (Thamel) and permits, day two on gentle acclimatization walks (Swayambhunath, Boudhanath) and real dal bhat. Withdraw rupees, grab a local SIM, and leave two buffer days after your trek.

## Day 1 — gear and permits

**Morning: Thamel gear shopping.** Thamel is the trekking-gear district — rental and purchase, both good quality. Rent a down jacket ($5/day), poles ($2/day), sleeping bag ($4/day). Buy trekking socks, energy bars, blister plasters. I'll take you to my trusted shops if you arrive the day before departure.

**Afternoon: Permits (only if self-organising).** Nepal Tourism Board office at Bhrikuti Mandap. Allow 2–3 hours. If you booked with me, this is already done — enjoy the free afternoon.

## Day 2 — acclimatization and culture

**Morning: Swayambhunath (Monkey Temple).** A 45-minute walk from Thamel, uphill — good light acclimatization, with city views and Tibetan Buddhist prayer wheels.

**Afternoon: Boudhanath Stupa.** The largest stupa in Nepal. Walk the kora clockwise — a calm place to get your head into the mountains.

**Evening: dal bhat in Thamel.** Not the tourist restaurants — ask me for my local spot. Rs. 250 (~$2) for a full plate no European restaurant can match.

## Practical notes

- Withdraw Nepali rupees here (best rates at bank ATMs, not exchange counters)
- Buy a local SIM (NTC or Ncell, Rs. 100–200 — works in most villages)
- Book your flight home with **two buffer days** after your trek ends

**Arriving soon and want my shop and food recommendations?** [Message me on WhatsApp](${WA}).
    `.trim(),
  },

  // ---------------------------------------------------------------------------
  // New posts covering the other treks now offered on the site.
  // ---------------------------------------------------------------------------
  {
    slug: 'everest-base-camp-guide',
    title: 'Everest Base Camp trek — how it really works',
    excerpt: 'The 16-day route to EBC and Kala Patthar, honestly: the Lukla flight, the acclimatization days that matter, the permits you need, and what the altitude really feels like.',
    category: 'Trail Guide',
    date: 'June 15, 2026',
    readTime: '7 min read',
    emoji: '🏔️',
    content: `
**Short version:** Everest Base Camp is a **16-day** trip. You fly to Lukla, walk up the Khumbu through Namche and Tengboche, stand at Base Camp (5,364m), and climb Kala Patthar (**5,545m** — the highest point and the best Everest view) at dawn. Two acclimatization days are non-negotiable. Small group, licensed guide, all permits handled.

## The shape of the trek

You start with an early drive to Ramechhap and a short mountain flight into **Lukla (2,840m)**, then trek up the Dudh Koshi to **Namche Bazaar (3,440m)** — the Sherpa capital. From there it's Tengboche's monastery, Dingboche, Lobuche, and Gorakshep, before the push to Everest Base Camp and the pre-dawn climb of Kala Patthar.

## Acclimatization — where people go wrong

The two rest days — at **Namche** and **Dingboche** — are the difference between finishing strong and getting turned around. You don't sit still on them; you hike high and sleep low. Skipping or rushing these is the single most common reason trekkers don't make it.

## Permits you need

- **Sagarmatha National Park Entry Permit** — issued by the Dept. of National Parks & Wildlife Conservation
- **Khumbu Pasang Lhamu Rural Municipality Permit** — issued by the local municipality

Both are included in your package and confirmed at current rates when you book — you don't chase paperwork.

## The honest bits

- **The Lukla flight** is weather-dependent. Build a buffer day into your trip home; delays happen.
- Headaches are common above 4,000m. That's normal — not making progress despite them isn't. I carry a pulse oximeter and monitor everyone daily.
- It's busier than Manaslu. If you want Everest *and* solitude, we can talk about quieter alternatives.

**Thinking about EBC?** [Message me on WhatsApp](${WA}) and I'll walk you through dates and fitness.
    `.trim(),
  },
  {
    slug: 'annapurna-circuit-vs-base-camp',
    title: 'Annapurna Circuit vs Annapurna Base Camp — which one?',
    excerpt: 'Two very different Annapurna treks. The Circuit is a big 16-day loop over a 5,416m pass; Base Camp is a shorter 13-day trek into a glacial amphitheatre. Here is how to choose.',
    category: 'Planning',
    date: 'June 8, 2026',
    readTime: '6 min read',
    emoji: '⚖️',
    content: `
**Short version:** The **Circuit** is the epic — 16 days, a big loop, over the Thorong La at **5,416m**, with huge landscape variety. **Base Camp (ABC)** is shorter and gentler — 13 days into the Annapurna Sanctuary at **4,130m**, ringed by 7,000m+ peaks. Less time or less altitude? ABC. Want the full journey and the high pass? The Circuit.

## Side by side

- **Days** — Circuit: ~16 · Base Camp: ~13
- **High point** — Circuit: Thorong La, 5,416m · Base Camp: the Sanctuary, 4,130m
- **Character** — Circuit: big loop, huge variety · Base Camp: into an amphitheatre of peaks
- **Highlights** — Circuit: Manang, Muktinath, Poon Hill · Base Camp: Machapuchare, ABC sunrise, hot springs
- **Logistics** — Circuit: longer, more remote · Base Camp: shorter, easy from Pokhara

## Choose the Circuit if you want

- The full arc — subtropical valleys to high alpine desert
- To cross a genuine 5,000m+ pass (Thorong La)
- Muktinath, the Kali Gandaki, and a Poon Hill sunrise finish

## Choose Base Camp if you want

- A shorter trip that still gets you deep into the high mountains
- A lower maximum altitude (easier on acclimatization)
- The Sanctuary — standing inside a ring of thirteen 7,000m peaks at sunrise
- Jhinu Danda's hot springs on the way down

## My take

Short on time or new to high altitude → **ABC**. Want the landmark journey and the pass → **the Circuit**. Both include a licensed guide, small groups, and all permits (ACAP + TIMS).

**Not sure which fits your dates and fitness?** [Message me on WhatsApp](${WA}).
    `.trim(),
  },
  {
    slug: 'altitude-sickness-nepal-treks',
    title: 'Altitude sickness on Nepal treks — a guide’s protocol',
    excerpt: 'How I actually keep clients safe at altitude: acclimatization done properly, the daily checks I run, when to take Diamox, and the one rule I never break — descend.',
    category: 'Planning',
    date: 'May 25, 2026',
    readTime: '6 min read',
    emoji: '⛑️',
    content: `
**Short version:** Altitude sickness is manageable, not mysterious. Climb slowly, sleep low, drink 4 litres a day, and treat any warning sign seriously. I carry a pulse oximeter on every trek and I have one unbreakable rule: if it's getting worse, we go down. Altitude always wins an argument.

## What it is

Above roughly 2,500m your body has less oxygen to work with. Mild acute mountain sickness (AMS) — headache, poor sleep, low appetite — is common and usually fine. The danger is when it progresses. The good news: it's very preventable with the right pace.

## How we prevent it

- **Slow ascent.** We don't gain sleeping altitude too fast. This is why acclimatization days exist — and why we keep them.
- **Climb high, sleep low.** Rest days aren't idle; we hike up and come back down to sleep.
- **Hydration.** Around 4 litres of water a day. Alcohol stays off the menu up high.
- **Daily monitoring.** I carry a **pulse oximeter** and check oxygen saturation, especially above 4,000m.

## When to take Diamox

Carry it on every high trek even if you don't plan to use it. Many trekkers start a preventive dose before the highest days; I'll advise based on how you're acclimatising. It's a help, not a substitute for a sensible pace.

## The rule I never break

Severe headache that won't clear, loss of coordination, or confusion → we **descend immediately**. No summit, no pass, is worth it, and coming down almost always fixes it fast. I also carry emergency contacts and know the evacuation options on every route — which is exactly why travel insurance covering helicopter evacuation is mandatory on my treks.

**Worried about altitude on your trek?** [Message me on WhatsApp](${WA}) — I'll talk you through the plan honestly.
    `.trim(),
  },
]
